import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  PanelRightClose,
  PanelRight,
  ChevronDown,
  ChevronRight,
  Users,
  CheckSquare,
  Tags,
  Paperclip,
  Plus,
  X,
  Circle,
  Clock,
  AlertTriangle,
  ListTodo,
} from 'lucide-react';
import { apiGet, apiPost, apiPatch } from '../lib/api';

/* ── Types ─────────────────────────────────────────────── */

interface Person {
  id: string;
  name: string;
  email: string | null;
  role: string | null;
  department: string | null;
}

interface Task {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  tag_ids: string[];
  scope_ids: string[];
  created_at: string;
}

interface DocumentSidebarProps {
  instanceId: string;
  seriesId: string;
  isOpen: boolean;
  onToggle: () => void;
  /** Current attendee IDs from the loaded meeting instance */
  attendeeIds: string[];
  /** Callback when attendee list changes (so parent can update its state) */
  onAttendeeIdsChange: (ids: string[]) => void;
}

/* ── Constants ─────────────────────────────────────────── */

const STATUS_ICONS: Record<string, React.ReactNode> = {
  todo: <Circle size={12} className="text-text-muted" />,
  in_progress: <Clock size={12} className="text-accent" />,
  blocked: <AlertTriangle size={12} className="text-danger" />,
  done: <CheckSquare size={12} className="text-success" />,
  cancelled: <X size={12} className="text-text-muted" />,
};

const PRIORITY_DOT_COLORS: Record<string, string> = {
  urgent: 'bg-danger',
  high: 'bg-warning',
  medium: 'bg-text-secondary',
  low: 'bg-text-muted',
};

/* ── Main Component ───────────────────────────────────── */

export function DocumentSidebar({
  instanceId,
  seriesId: _seriesId,
  isOpen,
  onToggle,
  attendeeIds,
  onAttendeeIdsChange,
}: DocumentSidebarProps) {
  // Section collapse state
  const [attendeesOpen, setAttendeesOpen] = useState(true);
  const [tasksOpen, setTasksOpen] = useState(true);
  const [tagsOpen, setTagsOpen] = useState(true);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);

  // Data
  const [allPeople, setAllPeople] = useState<Person[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(true);

  // Attendee dropdown
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [attendeeSearch, setAttendeeSearch] = useState('');
  const addAttendeeRef = useRef<HTMLDivElement>(null);
  const attendeeInputRef = useRef<HTMLInputElement>(null);

  // New task form
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const newTaskInputRef = useRef<HTMLInputElement>(null);

  /* ── Data Loading ────────────────────────────────────── */

  const loadSidebarData = useCallback(async () => {
    try {
      const [people, meetingTasks] = await Promise.all([
        apiGet<Person[]>('/people'),
        apiGet<Task[]>(`/tasks?source_meeting_id=${instanceId}`),
      ]);
      setAllPeople(people);
      setTasks(meetingTasks);
    } catch (err) {
      console.error('Failed to load sidebar data:', err);
    } finally {
      setLoadingTasks(false);
    }
  }, [instanceId]);

  useEffect(() => {
    if (isOpen) {
      setLoadingTasks(true);
      loadSidebarData();
    }
  }, [instanceId, isOpen, loadSidebarData]);

  /* ── Attendee Management ────────────────────────────── */

  async function addAttendee(personId: string) {
    if (attendeeIds.includes(personId)) return;
    const newIds = [...attendeeIds, personId];
    onAttendeeIdsChange(newIds);
    setShowAddAttendee(false);
    setAttendeeSearch('');
    try {
      await apiPatch(`/meeting-instances/${instanceId}`, { attendee_ids: newIds });
    } catch (err) {
      console.error('Failed to save attendees:', err);
      // Revert on failure
      onAttendeeIdsChange(attendeeIds);
    }
  }

  async function removeAttendee(personId: string) {
    const newIds = attendeeIds.filter(id => id !== personId);
    onAttendeeIdsChange(newIds);
    try {
      await apiPatch(`/meeting-instances/${instanceId}`, { attendee_ids: newIds });
    } catch (err) {
      console.error('Failed to save attendees:', err);
      // Revert on failure
      onAttendeeIdsChange(attendeeIds);
    }
  }

  // Close attendee dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (addAttendeeRef.current && !addAttendeeRef.current.contains(e.target as Node)) {
        setShowAddAttendee(false);
        setAttendeeSearch('');
      }
    }
    if (showAddAttendee) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [showAddAttendee]);

  // Focus input when attendee dropdown opens
  useEffect(() => {
    if (showAddAttendee && attendeeInputRef.current) {
      attendeeInputRef.current.focus();
    }
  }, [showAddAttendee]);

  /* ── Task Creation ──────────────────────────────────── */

  async function handleCreateTask() {
    const trimmed = newTaskTitle.trim();
    if (!trimmed || savingTask) return;
    setSavingTask(true);
    try {
      await apiPost('/tasks', {
        title: trimmed,
        source_meeting_id: instanceId,
        priority: 'medium',
      });
      setNewTaskTitle('');
      setShowNewTask(false);
      // Refresh tasks list
      const meetingTasks = await apiGet<Task[]>(`/tasks?source_meeting_id=${instanceId}`);
      setTasks(meetingTasks);
    } catch (err) {
      console.error('Failed to create task:', err);
    } finally {
      setSavingTask(false);
    }
  }

  // Focus new task input when it opens
  useEffect(() => {
    if (showNewTask && newTaskInputRef.current) {
      newTaskInputRef.current.focus();
    }
  }, [showNewTask]);

  /* ── Derived Data ───────────────────────────────────── */

  const peopleMap = new Map(allPeople.map(p => [p.id, p]));
  const attendees = attendeeIds
    .map(id => peopleMap.get(id))
    .filter((p): p is Person => p !== undefined);

  const availablePeople = allPeople.filter(
    p => !attendeeIds.includes(p.id) &&
      (attendeeSearch === '' || p.name.toLowerCase().includes(attendeeSearch.toLowerCase()))
  );

  /* ── Render ─────────────────────────────────────────── */

  return (
    <>
      {/* Toggle button — always visible */}
      <button
        onClick={onToggle}
        className="absolute top-0 right-0 p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors z-10"
        title={isOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        {isOpen ? <PanelRightClose size={18} /> : <PanelRight size={18} />}
      </button>

      {/* Sidebar panel */}
      <div
        className={`flex-shrink-0 border-l border-border bg-bg-secondary overflow-y-auto transition-all duration-200 ${
          isOpen ? 'w-[280px]' : 'w-0 overflow-hidden'
        }`}
      >
        <div className="w-[280px] p-4 space-y-1">
          {/* ── Attendees Section ──────────────────────── */}
          <SidebarSection
            title="Attendees"
            icon={<Users size={14} />}
            isOpen={attendeesOpen}
            onToggle={() => setAttendeesOpen(!attendeesOpen)}
            count={attendees.length}
          >
            <div className="space-y-1.5">
              {attendees.length === 0 && (
                <p className="text-xs text-text-muted py-1">No attendees added</p>
              )}
              {attendees.map(person => (
                <div
                  key={person.id}
                  className="flex items-center gap-2 group py-1 px-1 rounded hover:bg-bg-hover transition-colors"
                >
                  <div className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                    {getInitials(person.name)}
                  </div>
                  <span className="text-sm text-text-primary truncate flex-1">
                    {person.name}
                  </span>
                  <button
                    onClick={() => removeAttendee(person.id)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-text-muted hover:text-danger transition-all flex-shrink-0"
                    title="Remove attendee"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}

              {/* Add attendee */}
              <div className="relative" ref={addAttendeeRef}>
                {showAddAttendee ? (
                  <div>
                    <input
                      ref={attendeeInputRef}
                      type="text"
                      value={attendeeSearch}
                      onChange={e => setAttendeeSearch(e.target.value)}
                      placeholder="Search people..."
                      className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                      onKeyDown={e => {
                        if (e.key === 'Escape') {
                          setShowAddAttendee(false);
                          setAttendeeSearch('');
                        }
                      }}
                    />
                    {availablePeople.length > 0 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-36 overflow-y-auto bg-bg-surface border border-border rounded-lg shadow-lg">
                        {availablePeople.map(person => (
                          <button
                            key={person.id}
                            onClick={() => addAttendee(person.id)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-bg-hover transition-colors"
                          >
                            <div className="w-5 h-5 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[9px] font-semibold flex-shrink-0">
                              {getInitials(person.name)}
                            </div>
                            <span className="text-xs text-text-primary truncate">{person.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {availablePeople.length === 0 && attendeeSearch && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-bg-surface border border-border rounded-lg shadow-lg px-3 py-2">
                        <span className="text-xs text-text-muted">No matching people</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddAttendee(true)}
                    className="flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors py-1"
                  >
                    <Plus size={12} />
                    Add attendee
                  </button>
                )}
              </div>
            </div>
          </SidebarSection>

          {/* ── Related Tasks Section ──────────────────── */}
          <SidebarSection
            title="Related Tasks"
            icon={<ListTodo size={14} />}
            isOpen={tasksOpen}
            onToggle={() => setTasksOpen(!tasksOpen)}
            count={tasks.length}
          >
            <div className="space-y-1">
              {loadingTasks ? (
                <div className="flex items-center justify-center py-3">
                  <div className="animate-spin rounded-full h-4 w-4 border border-accent border-t-transparent" />
                </div>
              ) : tasks.length === 0 ? (
                <p className="text-xs text-text-muted py-1">No tasks linked to this meeting</p>
              ) : (
                tasks.map(task => (
                  <Link
                    key={task.id}
                    to={`/tasks/${task.id}`}
                    className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-bg-hover transition-colors group"
                  >
                    <span className="flex-shrink-0">
                      {STATUS_ICONS[task.status] ?? <Circle size={12} />}
                    </span>
                    <span
                      className={`text-xs flex-1 truncate ${
                        task.status === 'done'
                          ? 'line-through text-text-muted'
                          : 'text-text-primary group-hover:text-accent'
                      } transition-colors`}
                    >
                      {task.title}
                    </span>
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        PRIORITY_DOT_COLORS[task.priority] ?? 'bg-text-muted'
                      }`}
                      title={task.priority}
                    />
                  </Link>
                ))
              )}

              {/* New Task inline form */}
              {showNewTask ? (
                <div className="pt-1">
                  <input
                    ref={newTaskInputRef}
                    type="text"
                    value={newTaskTitle}
                    onChange={e => setNewTaskTitle(e.target.value)}
                    placeholder="Task title..."
                    disabled={savingTask}
                    className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent disabled:opacity-50"
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleCreateTask();
                      if (e.key === 'Escape') {
                        setShowNewTask(false);
                        setNewTaskTitle('');
                      }
                    }}
                  />
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <button
                      onClick={handleCreateTask}
                      disabled={!newTaskTitle.trim() || savingTask}
                      className="px-2 py-1 text-[10px] bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50 transition-colors"
                    >
                      {savingTask ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      onClick={() => { setShowNewTask(false); setNewTaskTitle(''); }}
                      className="px-2 py-1 text-[10px] text-text-muted hover:text-text-primary transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewTask(true)}
                  className="flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors py-1"
                >
                  <Plus size={12} />
                  New Task
                </button>
              )}
            </div>
          </SidebarSection>

          {/* ── Tags Section ──────────────────────────── */}
          <SidebarSection
            title="Tags"
            icon={<Tags size={14} />}
            isOpen={tagsOpen}
            onToggle={() => setTagsOpen(!tagsOpen)}
          >
            <p className="text-xs text-text-muted py-1">
              Tags from note content will appear here
            </p>
          </SidebarSection>

          {/* ── Attachments Section ───────────────────── */}
          <SidebarSection
            title="Attachments"
            icon={<Paperclip size={14} />}
            isOpen={attachmentsOpen}
            onToggle={() => setAttachmentsOpen(!attachmentsOpen)}
          >
            <p className="text-xs text-text-muted py-1">
              File attachments coming soon
            </p>
          </SidebarSection>
        </div>
      </div>
    </>
  );
}

/* ── Sidebar Section Wrapper ──────────────────────────── */

function SidebarSection({
  title,
  icon,
  isOpen,
  onToggle,
  count,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border pb-3 mb-3 last:border-b-0 last:pb-0 last:mb-0">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full text-left py-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wide hover:text-text-primary transition-colors"
      >
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {icon}
        <span className="flex-1">{title}</span>
        {count !== undefined && count > 0 && (
          <span className="text-[10px] font-normal text-text-muted bg-bg-hover rounded-full px-1.5 py-0.5">
            {count}
          </span>
        )}
      </button>
      {isOpen && <div className="mt-2">{children}</div>}
    </div>
  );
}

/* ── Utility ──────────────────────────────────────────── */

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.charAt(0).toUpperCase();
}
